import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiCalendar,
  FiArrowRight,
  FiCheckSquare,
  FiEdit3,
  FiImage,
  FiLink,
  FiMoreHorizontal,
  FiPlus,
  FiRefreshCcw,
  FiTrash2,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";
import {
  addTodoAttachments,
  completeTodo,
  confirmTodoCompletion,
  createTodo,
  deleteTodo,
  listTodoAssignees,
  listTodos,
  updateTodo,
} from "../../api/todos";
import { listProjects } from "../../api/productivity";
import PaginationControls from "../PaginationControls";
import { getCachedQuery, invalidateCachedQueries, peekCachedQuery } from "../../utils/queryCache";

const TODO_PAGE_SIZE = 10;
const MAX_TASK_IMAGES = 5;
const ALLOWED_TASK_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

const toMessage = (error, fallback) => {
  const message = error?.message || error?.response?.data?.message || fallback;
  return Array.isArray(message) ? message.join(", ") : message;
};

const formatDate = (value, fallback = "No due date") => {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const getAssigneeName = (assignee) =>
  assignee?.fullName ||
  `${assignee?.firstName || ""} ${assignee?.lastName || ""}`.trim() ||
  "Unnamed staff";

const getInitials = (name) =>
  `${name || "ST"}`
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "ST";

const getTodoTime = (value) => {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortTodosNewestFirst = (items) =>
  [...items].sort((first, second) => getTodoTime(second.createdAt) - getTodoTime(first.createdAt));

function TodoTable({ todos, completingId, statusSavingId, onComplete, onConfirm, onEdit, onDelete, onStatusChange }) {
  const [attachmentTodo, setAttachmentTodo] = useState(null);
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-100">
      <table className="w-full min-w-[820px] border-collapse text-left text-sm">
        <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="w-12 px-4 py-3"><span className="sr-only">Complete</span></th>
            <th className="px-3 py-3 font-semibold">Task</th>
            <th className="px-3 py-3 font-semibold">Assignee</th>
            <th className="px-3 py-3 font-semibold">Created date</th>
            <th className="px-3 py-3 font-semibold">Due date</th>
            <th className="px-3 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {todos.map((todo) => {
            const done = todo.status === "Done";
            const awaiting = todo.status === "Pending Confirmation";
            return (
              <tr key={todo.id} className={`${done ? "bg-emerald-50/40" : ""} transition hover:bg-slate-50`}>
                <td className="px-4 py-3 align-top">
                  {todo.canComplete ? (
                    <button type="button" onClick={() => onComplete(todo.id)} disabled={completingId === todo.id} className={`inline-flex h-5 w-5 items-center justify-center rounded border ${done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 bg-white hover:border-[#1234CD]"}`} aria-label={`Mark ${todo.title} as done`}>{done ? "✓" : ""}</button>
                  ) : (
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded border text-xs ${done ? "border-emerald-500 bg-emerald-500 text-white" : awaiting ? "border-amber-400 bg-amber-50 text-amber-600" : "border-slate-200 bg-slate-50"}`}>{done ? "✓" : awaiting ? "…" : ""}</span>
                  )}
                </td>
                <td className="max-w-[280px] px-3 py-3 align-top">
                  <p className={`font-semibold ${done ? "text-emerald-700 line-through" : "text-slate-900"}`}>{todo.title}</p>
                  {todo.note ? <p className="mt-1 line-clamp-2 text-xs text-slate-500">{todo.note}</p> : null}
                  {todo.project ? <span className="mt-1.5 inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">{todo.project.title}</span> : null}
                  {todo.attachments?.length ? <button type="button" onClick={() => setAttachmentTodo(todo)} className="ml-1 mt-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:border-[#1234CD] hover:text-[#1234CD]" title={`View ${todo.attachments.length} attachments`} aria-label={`View attachments for ${todo.title}`}><FiMoreHorizontal /></button> : null}
                </td>
                <td className="whitespace-nowrap px-3 py-3 align-top text-xs text-slate-600"><span className="inline-flex items-center gap-1.5 font-medium"><FiUser />{getAssigneeName(todo.assignee)}</span>{todo.watchers?.length ? <p className="mt-1 text-[10px] text-slate-400">{todo.watchers.length} reviewer{todo.watchers.length === 1 ? "" : "s"} notified</p> : null}</td>
                <td className="whitespace-nowrap px-3 py-3 align-top text-xs text-slate-600"><span className="inline-flex items-center gap-1.5"><FiCalendar />{formatDate(todo.createdAt, "Date unavailable")}</span></td>
                <td className="whitespace-nowrap px-3 py-3 align-top text-xs text-slate-600"><span className="inline-flex items-center gap-1.5"><FiCalendar />{formatDate(todo.dueDate)}</span></td>
                <td className="px-3 py-3 align-top">
                  <div className="flex flex-col items-start gap-2">
                    {completingId === todo.id ? <span className="text-[11px] font-semibold text-[#1234CD]">Saving done status…</span> : null}
                    {(!todo.canUpdateStatus || awaiting) ? <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ${done ? "bg-emerald-100 text-emerald-700" : todo.status === "Blocked" ? "bg-red-100 text-red-700" : todo.status === "In Progress" ? "bg-blue-100 text-blue-700" : awaiting ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{todo.status === "Open" ? "To do" : todo.status}</span> : null}
                    {todo.canUpdateStatus && !awaiting ? <select value={todo.status} disabled={statusSavingId === todo.id || completingId === todo.id} onChange={(event) => onStatusChange(todo, event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 outline-none focus:border-[#1234CD] disabled:opacity-60" aria-label={`Change status for ${todo.title}`}><option value="Open">To do</option><option value="In Progress">In progress</option><option value="Blocked">Blocked</option>{(todo.canComplete || done) ? <option value="Done">Done</option> : null}</select> : null}
                  </div>
                </td>
                <td className="px-4 py-3 align-top"><div className="flex justify-end gap-1.5">
                  {todo.canConfirm ? <button type="button" onClick={() => onConfirm(todo.id)} disabled={completingId === todo.id} className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white disabled:opacity-60">Confirm</button> : null}
                  {todo.canEdit ? <button type="button" onClick={() => onEdit(todo)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 px-2.5 py-1.5 text-[11px] font-semibold text-[#1234CD] hover:bg-blue-50"><FiEdit3 />Edit</button> : null}
                  {todo.canDelete && todo.canEdit ? <button type="button" onClick={() => onDelete(todo)} className="rounded-lg border border-red-200 p-1.5 text-red-600 hover:bg-red-50" aria-label={`Delete ${todo.title}`}><FiTrash2 /></button> : null}
                </div></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {attachmentTodo ? <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/40 p-4" onMouseDown={() => setAttachmentTodo(null)}><div role="dialog" aria-modal="true" aria-label="Task attachments" onMouseDown={(event) => event.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl"><div className="flex items-start justify-between gap-3"><div><h3 className="font-bold text-[#071C50]">Task attachments</h3><p className="mt-1 line-clamp-1 text-xs text-slate-500">{attachmentTodo.title}</p></div><button type="button" onClick={() => setAttachmentTodo(null)} className="rounded-lg border px-3 py-1.5 text-xs font-semibold text-slate-600">Close</button></div><div className="mt-4 grid max-h-[60vh] gap-3 overflow-y-auto sm:grid-cols-2">{attachmentTodo.attachments.map((attachment, index) => attachment.type === "image" ? <a key={`${attachment.url}-${index}`} href={attachment.url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border"><img src={attachment.url} alt={`Task attachment ${index + 1}`} className="h-40 w-full object-cover"/><span className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-[#1234CD]"><FiImage/>Open image</span></a> : <a key={`${attachment.url}-${index}`} href={attachment.url} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-3 rounded-xl border p-3 text-sm font-semibold text-[#1234CD] hover:bg-blue-50"><FiLink className="shrink-0"/><span className="truncate">{attachment.url}</span></a>)}</div></div></div> : null}
    </div>
  );
}

export default function TodoPanel({
  compact = false,
  showTeam = true,
  title = "Todos",
  className = "",
  onTodosChanged,
  statusFilter = "all",
  compactLimit = 4,
  viewMoreTo = "",
  projectId = "",
  assignmentRequest = null,
  showScopeTabs = false,
}) {
  const [todos, setTodos] = useState([]);
  const [todoSummary, setTodoSummary] = useState({ open: 0, done: 0 });
  const [assignees, setAssignees] = useState([]);
  const [canAssignOthers, setCanAssignOthers] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [completingId, setCompletingId] = useState("");
  const [statusSavingId, setStatusSavingId] = useState("");
  const [pendingCompletion, setPendingCompletion] = useState(null);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: TODO_PAGE_SIZE, total: 0, totalPages: 1 });
  const [scopeMode, setScopeMode] = useState(showScopeTabs ? "mine" : showTeam ? "team" : "mine");
  const [completionView, setCompletionView] = useState(statusFilter === "done" ? "completed" : "active");
  const titleInputRef = useRef(null);
  const processedAssignmentRequestRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    note: "",
    dueDate: "",
    tags: "",
    attachmentUrls: "",
    attachmentFiles: [],
    ownerId: "",
    watcherIds: [],
    projectId,
  });

  const sortedTodos = useMemo(() => sortTodosNewestFirst(todos), [todos]);
  const projectTodos = projectId
    ? sortedTodos.filter((todo) => (todo.project?.id || todo.projectId) === projectId)
    : sortedTodos;
  const visibleTodos = compact ? projectTodos.slice(0, compactLimit) : projectTodos;
  const openTodos = todos.filter((todo) => todo.status !== "Done");
  const doneTodos = todos.filter((todo) => todo.status === "Done");
  const defaultAssigneeId = useMemo(() => {
    const self = assignees.find((assignee) => assignee.isSelf);
    return self?.id || assignees[0]?.id || "";
  }, [assignees]);
  const eligibleAssignees = useMemo(() => {
    if (!form.projectId) return assignees;
    const project = projects.find((item) => item.id === form.projectId);
    const memberIds = new Set((project?.assignees || []).map((item) => item.id));
    return assignees.filter((item) => memberIds.has(item.id));
  }, [assignees, form.projectId, projects]);

  const applyLoadedData = useCallback(({ todosResponse, assigneesResponse, projectsResponse }) => {
    const nextAssignees = Array.isArray(assigneesResponse?.assignees) ? assigneesResponse.assignees : [];
    const selfId = nextAssignees.find((item) => item.isSelf)?.id || "";
    const responseTodos = Array.isArray(todosResponse?.todos) ? todosResponse.todos : [];
    const nextTodos = showTeam ? responseTodos : responseTodos.filter((todo) => todo.assigneeId === selfId || todo.assignee?.id === selfId);
    const ownSummary = { open: nextTodos.filter((todo) => todo.status !== "Done").length, done: nextTodos.filter((todo) => todo.status === "Done").length };

    setTodos(nextTodos);
    setPagination(todosResponse?.pagination || { page, limit: compact ? compactLimit : TODO_PAGE_SIZE, total: nextTodos.length, totalPages: 1 });
    setTodoSummary(showTeam ? (todosResponse?.summary || { open: 0, done: 0 }) : ownSummary);
    setAssignees(nextAssignees);
    setCanAssignOthers(assigneesResponse?.canAssignOthers === true);
    setProjects(Array.isArray(projectsResponse?.projects) ? projectsResponse.projects : []);
    setForm((current) => ({ ...current, ownerId: current.ownerId || nextAssignees.find((item) => item.isSelf)?.id || nextAssignees[0]?.id || "" }));
  }, [compact, compactLimit, page, showTeam]);

  const loadTodos = useCallback(async ({ silent = false, force = false } = {}) => {
    const limit = compact ? compactLimit : TODO_PAGE_SIZE;
    const todoCacheKey = `todo-list:${projectId || "all"}:${scopeMode}:${completionView}:${page}:${limit}`;
    const cached = !force ? peekCachedQuery(todoCacheKey) : null;
    if (cached) {
      applyLoadedData(cached);
      setLoading(false);
      setError("");
      return;
    }
    try {
      if (!silent) {
        setLoading(true);
      }
      setError("");

      const loaded = await getCachedQuery(todoCacheKey, async () => {
        const [todosResponse, assigneesResponse, projectsResponse] = await Promise.all([
          listTodos({ page, limit, scope: scopeMode, status: completionView, ...(projectId ? { projectId } : {}) }),
          getCachedQuery("todos:support:assignees", listTodoAssignees),
          getCachedQuery("todos:support:projects", listProjects),
        ]);
        return { todosResponse, assigneesResponse, projectsResponse };
      }, { force });
      applyLoadedData(loaded);
    } catch (requestError) {
      setError(toMessage(requestError, "Unable to load todos."));
    } finally {
      setLoading(false);
    }
  }, [applyLoadedData, compact, compactLimit, completionView, page, projectId, scopeMode]);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  useEffect(() => {
    const assigneeId = assignmentRequest?.assigneeId;
    if (!assigneeId || loading) return;
    const requestKey = assignmentRequest.requestId || assigneeId;
    if (processedAssignmentRequestRef.current === requestKey) return;
    processedAssignmentRequestRef.current = requestKey;

    const isEligible = eligibleAssignees.some((assignee) => assignee.id === assigneeId);
    if (!isEligible) {
      setError("This project member is not available for task assignment.");
      return;
    }

    setError("");
    setEditingTodo(null);
    setForm({
      title: "",
      note: "",
      dueDate: "",
      tags: "",
      attachmentUrls: "",
      attachmentFiles: [],
      ownerId: assigneeId,
      watcherIds: [],
      projectId,
    });
    setShowCreateForm(true);
    requestAnimationFrame(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [assignmentRequest, eligibleAssignees, loading, projectId]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      setError("Todo title is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const tags = form.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);

      const payload = {
        title: form.title.trim(),
        note: form.note.trim() || undefined,
        dueDate: editingTodo ? (form.dueDate || null) : (form.dueDate || undefined),
        tags,
        ...(!editingTodo ? { attachmentUrls: form.attachmentUrls.split(/[\n,]+/).map((url) => url.trim()).filter(Boolean) } : {}),
      };
      if (editingTodo) await updateTodo(editingTodo.id, payload);
      else {
        const created = await createTodo({
          ...payload,
          assigneeId: form.ownerId || defaultAssigneeId,
          watcherIds: form.watcherIds.filter((id) => id !== (form.ownerId || defaultAssigneeId)),
          projectId: form.projectId || undefined,
        });
        if (form.attachmentFiles.length) {
          try {
            await addTodoAttachments(created?.todo?.id, form.attachmentFiles);
          } catch (uploadError) {
            toast.warning(`Task created, but images could not be uploaded: ${toMessage(uploadError, "Upload failed")}`);
          }
        }
      }

      setForm({
        title: "",
        note: "",
        dueDate: "",
        tags: "",
        attachmentUrls: "",
        attachmentFiles: [],
        ownerId: defaultAssigneeId,
        watcherIds: [],
        projectId,
      });
      toast.success(editingTodo ? "Task updated successfully" : "Todo created successfully");
      invalidateCachedQueries("todo-list:");
      await loadTodos({ silent: true, force: true });
      setShowCreateForm(false);
      setEditingTodo(null);
      onTodosChanged?.();
    } catch (requestError) {
      setError(toMessage(requestError, "Unable to create todo."));
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (todo) => {
    setEditingTodo(todo);
    setForm({
      title: todo.title || "",
      note: todo.note || "",
      dueDate: todo.dueDate ? new Date(todo.dueDate).toISOString().slice(0, 10) : "",
      tags: Array.isArray(todo.tags) ? todo.tags.join(", ") : "",
      attachmentUrls: "",
      attachmentFiles: [],
      ownerId: todo.assigneeId || "",
      watcherIds: (todo.watchers || []).map((watcher) => watcher.id),
      projectId: todo.project?.id || projectId || "",
    });
    setShowCreateForm(true);
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingTodo(null);
    setForm({ title: "", note: "", dueDate: "", tags: "", attachmentUrls: "", attachmentFiles: [], ownerId: defaultAssigneeId, watcherIds: [], projectId });
  };

  const handleComplete = async (todoId, action = "submit") => {
    const previousTodos = todos;
    try {
      setCompletingId(todoId);
      setError("");
      setTodos((items) => items.map((todo) => todo.id === todoId ? { ...todo, status: "Done", completedAt: new Date().toISOString() } : todo));
      const response = action === "confirm"
        ? await confirmTodoCompletion(todoId)
        : await completeTodo(todoId);
      const completionMessage = response?.message || (action === "confirm" ? "Completion confirmed" : "Todo marked as done");
      toast.success(response?.pointsAwarded > 0 ? `${completionMessage} · +${response.pointsAwarded} points` : completionMessage);
      invalidateCachedQueries("todo-list:");
      await loadTodos({ silent: true, force: true });
      onTodosChanged?.();
      return true;
    } catch (requestError) {
      setTodos(previousTodos);
      setError(toMessage(requestError, "Unable to complete todo."));
      return false;
    } finally {
      setCompletingId("");
    }
  };

  const handleStatusChange = async (todo, status) => {
    if (status === "Done") {
      setPendingCompletion({ id: todo.id, action: "submit" });
      return;
    }
    try {
      setStatusSavingId(todo.id);
      setError("");
      await updateTodo(todo.id, { status });
      invalidateCachedQueries("todo-list:");
      await loadTodos({ silent: true, force: true });
      onTodosChanged?.();
      toast.success(`Task moved to ${status === "Open" ? "To do" : status}`);
    } catch (requestError) {
      setError(toMessage(requestError, "Unable to update task status."));
    } finally {
      setStatusSavingId("");
    }
  };

  const confirmCompleteTodo = async () => {
    if (!pendingCompletion?.id) {
      return;
    }

    const completion = pendingCompletion;
    setPendingCompletion(null);
    await handleComplete(completion.id, completion.action);
  };

  const confirmDeleteTodo = async () => {
    if (!pendingDeletion?.id) return;

    try {
      setDeletingId(pendingDeletion.id);
      setError("");
      const response = await deleteTodo(pendingDeletion.id);
      toast.success(response?.message || "Task deleted successfully");
      setPendingDeletion(null);
      invalidateCachedQueries("todo-list:");
      await loadTodos({ silent: true, force: true });
      onTodosChanged?.();
    } catch (requestError) {
      setError(toMessage(requestError, "Unable to delete task."));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className={`min-w-0 bg-white border border-[#E6E4F0] rounded-2xl p-4 sm:p-5 shadow-sm ${className}`}>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <FiCheckSquare className="h-5 w-5 text-[#1234CD]" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500">
              {todoSummary.open ?? openTodos.length} open, {todoSummary.done ?? doneTodos.length} done
            </p>
          </div>
        </div>

        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          {!showCreateForm ? <button type="button" onClick={() => { setEditingTodo(null); setShowCreateForm(true); }} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#1234CD] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0f2fb3] sm:flex-none"><FiPlus />New task</button> : null}
          <button type="button" onClick={() => loadTodos({ force: true })} disabled={loading} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50" title="Refresh todos">
            <FiRefreshCcw className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        {showScopeTabs ? (
          <div className="inline-flex w-fit rounded-xl bg-slate-100 p-1" aria-label="Task ownership filter">
            {[{ value: "mine", label: "My tasks" }, { value: "team", label: "All tasks" }].map((option) => (
              <button key={option.value} type="button" onClick={() => { setScopeMode(option.value); setPage(1); }} className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${scopeMode === option.value ? "bg-white text-[#1234CD] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{option.label}</button>
            ))}
          </div>
        ) : <span />}
        <div className="inline-flex w-fit rounded-xl border border-slate-200 bg-white p-1" aria-label="Task completion filter">
          {[{ value: "active", label: "Active" }, { value: "completed", label: "Completed" }].map((option) => (
            <button key={option.value} type="button" onClick={() => { setCompletionView(option.value); setPage(1); }} className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${completionView === option.value ? "bg-[#EEF3FF] text-[#1234CD]" : "text-slate-500 hover:text-slate-800"}`}>{option.label}</button>
          ))}
        </div>
      </div>

      {error ? (
        <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {showCreateForm ? <>
      <form onSubmit={handleSubmit} className="mb-5 grid w-full min-w-0 max-w-full gap-3 overflow-hidden rounded-2xl border border-blue-100 bg-blue-50/30 p-3 sm:p-4">
        {editingTodo ? <div><p className="text-sm font-semibold text-[#071C50]">Edit task</p><p className="text-xs text-slate-500">Correct the task title, description, due date, or tags.</p></div> : null}
        <p className="min-w-0 break-words text-xs leading-relaxed text-gray-500">
          {canAssignOthers
            ? "Choose one task owner. Add reviewers only when they need progress notifications and visibility."
            : "Create and track your personal todos here."}
        </p>
        {editingTodo && canAssignOthers ? <div className="grid min-w-0 gap-3 rounded-xl border border-gray-200 p-3"><label className="block min-w-0"><span className="mb-1.5 block text-xs font-semibold text-gray-600">Task owner *</span><select value={form.ownerId} onChange={(event) => setForm(current => ({ ...current, ownerId: event.target.value, watcherIds: current.watcherIds.filter(id => id !== event.target.value) }))} className="box-border w-full min-w-0 max-w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"><option value="">Select the person responsible</option>{eligibleAssignees.map(assignee => <option key={assignee.id} value={assignee.id}>{assignee.isSelf ? "Me" : getAssigneeName(assignee)}</option>)}</select></label><div className="min-w-0"><div className="mb-1.5 flex min-w-0 flex-wrap items-center justify-between gap-1"><p className="min-w-0 text-xs font-semibold text-gray-600">Notify / review participants</p><span className="shrink-0 text-[11px] text-[#1234CD]">{form.watcherIds.length} selected</span></div><div className="max-h-28 space-y-1 overflow-y-auto">{eligibleAssignees.filter(assignee => assignee.id !== form.ownerId).map(assignee => <label key={assignee.id} className="flex min-w-0 items-center gap-2 break-words px-2 py-1.5 text-sm"><input type="checkbox" className="shrink-0" checked={form.watcherIds.includes(assignee.id)} onChange={() => setForm(current => ({ ...current, watcherIds: current.watcherIds.includes(assignee.id) ? current.watcherIds.filter(id => id !== assignee.id) : [...current.watcherIds, assignee.id] }))} />{assignee.isSelf ? "Me" : getAssigneeName(assignee)}</label>)}</div></div></div> : null}
        <div className={`grid grid-cols-1 gap-3 ${canAssignOthers ? "md:grid-cols-2" : ""}`}>
          <input
            ref={titleInputRef}
            type="text"
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            placeholder="Add a todo"
            className="box-border w-full min-w-0 max-w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1234CD]"
            maxLength={160}
          />

          {canAssignOthers && !editingTodo ? <div className="grid min-w-0 gap-3 rounded-xl border border-gray-200 p-3"><label className="block min-w-0"><span className="mb-1.5 block text-xs font-semibold text-gray-600">Task owner *</span><select value={form.ownerId} onChange={(event) => setForm(current => ({...current, ownerId: event.target.value, watcherIds: current.watcherIds.filter(id => id !== event.target.value)}))} className="box-border w-full min-w-0 max-w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#1234CD]"><option value="">Select the person responsible</option>{eligibleAssignees.map(assignee=><option key={assignee.id} value={assignee.id}>{assignee.isSelf?"Me":getAssigneeName(assignee)}</option>)}</select></label><div className="min-w-0"><div className="mb-1.5 flex min-w-0 flex-wrap items-center justify-between gap-1"><p className="min-w-0 text-xs font-semibold text-gray-600">Notify / review participants</p><span className="shrink-0 text-[11px] text-[#1234CD]">{form.watcherIds.length} selected</span></div><p className="mb-2 break-words text-[11px] leading-relaxed text-slate-400">They can follow updates, but cannot change or complete the task.</p><div className="max-h-28 space-y-1 overflow-y-auto">{eligibleAssignees.filter(assignee=>assignee.id!==form.ownerId).map(assignee=><label key={assignee.id} className="flex min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-slate-50"><input className="shrink-0" type="checkbox" checked={form.watcherIds.includes(assignee.id)} onChange={()=>setForm(current=>({...current,watcherIds:current.watcherIds.includes(assignee.id)?current.watcherIds.filter(id=>id!==assignee.id):[...current.watcherIds,assignee.id]}))}/><span className="min-w-0 break-words">{assignee.isSelf?"Me":getAssigneeName(assignee)}</span></label>)}</div></div></div> : null}
        </div>

        <select disabled={Boolean(projectId) || Boolean(editingTodo)} value={form.projectId} onChange={(event) => { const projectId=event.target.value; const project=projects.find(item=>item.id===projectId); const memberIds=new Set((project?.assignees||[]).map(item=>item.id)); setForm(current => ({...current, projectId, ownerId: projectId&& !memberIds.has(current.ownerId)?"":current.ownerId, watcherIds: projectId ? current.watcherIds.filter(id=>memberIds.has(id)) : current.watcherIds})); }} className="box-border w-full min-w-0 max-w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1234CD] disabled:bg-slate-100 disabled:text-slate-600">
          <option value="">No project</option>
          {projects.map(project => <option key={project.id} value={project.id}>{project.title}</option>)}
        </select>

        {!compact || editingTodo ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input
              type="date"
              value={form.dueDate}
              onChange={(event) => setForm((current) => ({ ...current, dueDate: event.target.value }))}
              className="box-border w-full min-w-0 max-w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1234CD]"
            />
            <input
              type="text"
              value={form.tags}
              onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))}
              placeholder="Tags, comma separated"
              className="box-border w-full min-w-0 max-w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1234CD]"
            />
            <input
              type="text"
              value={form.note}
              onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
              placeholder="Optional note"
              className="box-border w-full min-w-0 max-w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1234CD]"
              maxLength={1000}
            />
            {!editingTodo ? <div className="grid gap-2 rounded-xl border border-gray-200 bg-white p-3">
              <textarea
                value={form.attachmentUrls}
                onChange={(event) => setForm((current) => ({ ...current, attachmentUrls: event.target.value }))}
                placeholder="Web links, one per line"
                rows={2}
                className="box-border w-full min-w-0 max-w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#1234CD]"
              />
              <label className="cursor-pointer rounded-lg border border-dashed border-blue-200 bg-blue-50/60 px-3 py-2 text-center text-xs font-semibold text-[#1234CD] hover:bg-blue-50">
                Upload images (up to 5 at a time)
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="sr-only" onChange={(event) => {
                  const selected = Array.from(event.target.files || []);
                  const combined = [...form.attachmentFiles, ...selected].slice(0, MAX_TASK_IMAGES);
                  const invalid = combined.find((file) => !ALLOWED_TASK_IMAGE_TYPES.has(file.type));
                  if (invalid) {
                    setError(`${invalid.name} must be a JPEG, PNG, WebP, or GIF image.`);
                  } else {
                    setError("");
                    setForm((current) => ({ ...current, attachmentFiles: combined }));
                  }
                  event.target.value = "";
                }}/>
              </label>
              {form.attachmentFiles.length ? <div className="flex flex-wrap gap-2">{form.attachmentFiles.map((file, index) => <span key={`${file.name}-${file.lastModified}-${index}`} className="inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600"><FiImage className="shrink-0"/><span className="max-w-[180px] truncate">{file.name}</span><button type="button" onClick={() => setForm((current) => ({ ...current, attachmentFiles: current.attachmentFiles.filter((_, fileIndex) => fileIndex !== index) }))} aria-label={`Remove ${file.name}`} className="rounded-full p-0.5 hover:bg-slate-200"><FiX/></button></span>)}</div> : null}
            </div> : null}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={closeForm} disabled={saving} className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving || !assignees.length || !form.ownerId} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1234CD] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0f2fb3] disabled:cursor-not-allowed disabled:opacity-60">
            {saving ? <FiRefreshCcw className="animate-spin" /> : <FiPlus />}
            {saving ? (editingTodo ? "Saving..." : "Creating...") : editingTodo ? "Save changes" : canAssignOthers ? "Create Task" : "Create My Task"}
          </button>
        </div>
      </form>

      {showTeam && canAssignOthers && !editingTodo ? (
        <div className="mb-5 rounded-xl bg-[#F8FAFF] p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#071C50]">
            <FiUsers />
            Department members you can assign
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {assignees.map((assignee) => {
              const name = getAssigneeName(assignee);
              return (
                <div key={assignee.id} className="min-w-[160px] rounded-lg border border-gray-200 bg-white p-3">
                  <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#EEF2FF] text-xs font-bold text-[#1234CD]">
                    {getInitials(name)}
                  </div>
                  <p className="truncate text-sm font-semibold text-gray-900">{assignee.isSelf ? `${name} (Me)` : name}</p>
                  <p className="truncate text-xs text-gray-500">{assignee.positionName || assignee.assignmentLabel || "Staff"}</p>
                  <p className="truncate text-xs text-gray-400">{assignee.departmentName || "Department"}</p>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      </> : null}

      <div className={compact ? "max-h-[270px] overflow-y-auto pr-1" : "space-y-3"}>
        {loading ? (
          <p className="py-6 text-center text-sm text-gray-500">Loading todos...</p>
        ) : visibleTodos.length > 0 ? (
          <TodoTable todos={visibleTodos} completingId={completingId} statusSavingId={statusSavingId} onComplete={(id) => setPendingCompletion({ id, action: "submit" })} onConfirm={(id) => setPendingCompletion({ id, action: "confirm" })} onEdit={beginEdit} onDelete={setPendingDeletion} onStatusChange={handleStatusChange} />
        ) : false ? (
          visibleTodos.map((todo) => {
            const done = todo.status === "Done";
            const awaiting = todo.status === "Pending Confirmation";
            const assigneeName = getAssigneeName(todo.assignee);

            return (
              <div
                key={todo.id}
                className={`mb-2 rounded-xl border p-3 transition ${
                  done ? "border-emerald-100 bg-emerald-50/60" : "border-[#E8E7F5] bg-[#F9F8FF]"
                }`}
              >
                <div className="flex items-start gap-3">
                  {todo.canComplete ? <button
                    type="button"
                    onClick={() => setPendingCompletion({ id: todo.id, action: "submit" })}
                    disabled={completingId === todo.id}
                    className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      done ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-300 bg-white text-white hover:border-[#1234CD]"
                    } disabled:cursor-not-allowed`}
                    title="Mark as done"
                  >
                    <span className="text-xs">✓</span>
                  </button> : <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs ${done ? "border-emerald-500 bg-emerald-500 text-white" : awaiting ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 bg-gray-50 text-gray-300"}`}>{done ? "✓" : awaiting ? "…" : ""}</span>}

                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-semibold ${done ? "text-emerald-700 line-through" : "text-gray-900"}`}>
                      {todo.title}
                    </p>
                    {todo.note ? <p className="mt-1 text-xs text-gray-500 line-clamp-2">{todo.note}</p> : null}
                    {todo.project ? <span className="mt-2 inline-flex rounded-full bg-violet-50 px-2 py-1 text-[11px] font-semibold text-violet-700">{todo.project.title}</span> : null}
                    {awaiting ? <span className="mt-2 ml-1 inline-flex rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700">Awaiting assigner confirmation</span> : null}

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${done ? "bg-emerald-100 text-emerald-700" : todo.status === "Blocked" ? "bg-red-100 text-red-700" : todo.status === "In Progress" ? "bg-blue-100 text-blue-700" : awaiting ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {todo.status === "Open" ? "To do" : todo.status}
                      </span>
                      {todo.canUpdateStatus && !awaiting ? <select value={todo.status} disabled={statusSavingId === todo.id || completingId === todo.id} onChange={(event) => handleStatusChange(todo, event.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 outline-none focus:border-[#1234CD] disabled:opacity-60" aria-label={`Change status for ${todo.title}`}>
                        <option value="Open">To do</option>
                        <option value="In Progress">In progress</option>
                        <option value="Blocked">Blocked</option>
                        {(todo.canComplete || done) ? <option value="Done">Done</option> : null}
                      </select> : null}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <FiUser />
                        {assigneeName}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <FiCalendar />
                        Created {formatDate(todo.createdAt, "date unavailable")}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <FiCalendar />
                        {formatDate(todo.dueDate)}
                      </span>
                    </div>

                    {todo.tags?.length ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {todo.tags.map((tag) => (
                          <span key={`${todo.id}-${tag}`} className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-[#1234CD]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {todo.canConfirm ? <button type="button" onClick={() => setPendingCompletion({ id: todo.id, action: "confirm" })} disabled={completingId === todo.id} className="mt-3 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60">Confirm completion &amp; award points</button> : null}
                    {todo.canEdit ? <button type="button" onClick={() => beginEdit(todo)} className="mt-3 ml-2 inline-flex items-center gap-1 rounded-lg border border-blue-200 px-3 py-2 text-xs font-semibold text-[#1234CD] hover:bg-blue-50"><FiEdit3 />Edit task</button> : null}
                    {todo.canDelete ? <button type="button" onClick={() => setPendingDeletion(todo)} className="mt-3 ml-2 inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"><FiTrash2 />Delete task</button> : null}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p className="py-6 text-center text-sm text-gray-500">No todos yet.</p>
        )}
      </div>

      {!compact ? <PaginationControls page={pagination.page || page} pageSize={pagination.limit || TODO_PAGE_SIZE} totalItems={pagination.total || 0} onPageChange={setPage} /> : null}

      {compact && viewMoreTo ? (
        <Link
          to={viewMoreTo}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#D9E3FF] bg-[#F7F9FF] px-4 py-2.5 text-sm font-semibold text-[#1234CD] transition hover:border-[#1234CD] hover:bg-[#EEF2FF]"
        >
          View more tasks
          <FiArrowRight aria-hidden="true" />
        </Link>
      ) : null}

      {pendingCompletion ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
            <p className="text-lg font-semibold text-gray-900">{pendingCompletion.action === "confirm" ? "Confirm this completion?" : "Mark this todo as done?"}</p>
            <p className="mt-2 text-sm text-gray-500">
              {pendingCompletion.action === "confirm"
                ? "This confirms the employee’s work and awards points for this assigned task."
                : "Assigned work will be sent to the assigner for confirmation. Eligible self-assigned HOD tasks earn points immediately."}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingCompletion(null)}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmCompleteTodo}
                disabled={completingId === pendingCompletion.id}
                className="rounded-lg bg-[#1234CD] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0f2fb3] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {completingId === pendingCompletion.id ? "Saving..." : pendingCompletion.action === "confirm" ? "Confirm & award" : "Mark done"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingDeletion ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="delete-task-title">
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-red-600"><FiTrash2 className="h-5 w-5" /></div>
            <p id="delete-task-title" className="mt-4 text-lg font-semibold text-gray-900">Delete this task?</p>
            <p className="mt-2 text-sm text-gray-500">This permanently removes the task for {getAssigneeName(pendingDeletion.assignee)}. This action cannot be undone.</p>
            <p className="mt-3 line-clamp-2 rounded-xl bg-slate-50 p-3 text-sm font-medium text-slate-700">{pendingDeletion.title}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" disabled={Boolean(deletingId)} onClick={() => setPendingDeletion(null)} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">Cancel</button>
              <button type="button" disabled={Boolean(deletingId)} onClick={confirmDeleteTodo} className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-wait disabled:opacity-60">{deletingId ? "Deleting..." : "Delete task"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
